// Automation workflows — n8n pipelines that run on a schedule without me.
// Same shape of record as `projects`, but the interesting part of a workflow is
// its pipeline, not its screenshots, so each one carries an ordered `stages`
// list and a small `facts` table instead of images.

export const workflows = [
  {
    id: "job-board-aggregator",
    tone: "teal",
    name: "Job Board Aggregator → Email Digest",
    type: "n8n workflow · self-hosted",
    cadence: "Daily · 08:00",
    problem:
      "Checking eight job boards every morning is slow and easy to skip, and most remote listings are geo-locked to the US, EU or LATAM — unusable from South Africa. This scans all eight, keeps only the roles that are both eligible and relevant, and emails one clean digest.",
    stages: [
      {
        label: "Trigger",
        detail: "Cron fires at 08:00 every day.",
      },
      {
        label: "Collect",
        detail:
          "Eight remote job / freelance sources fanned out in parallel — RemoteOK, three WeWorkRemotely feeds, Remotive, Himalayas, Jobicy and Hacker News (jobs plus \"seeking freelancer\" comments), mixing RSS and JSON APIs.",
      },
      {
        label: "Normalize",
        detail:
          "Every source is mapped to the same shape — title, url, location, source, kind — so the rest of the pipeline never has to care where a listing came from.",
      },
      {
        label: "Filter",
        detail:
          "Two independent axes. Eligibility reads the structured location field where a source exposes one, and falls back to a geo-exclusion blocklist over the listing text where it doesn't. Relevance matches a full-stack keyword set.",
      },
      {
        label: "Dedupe",
        detail:
          "A Google Sheet of already-sent roles acts as memory, so the same job is never mailed twice.",
      },
      {
        label: "Deliver",
        detail:
          "Nothing new, no email. Otherwise a digest split into Remote Jobs and Freelance Gigs goes out over SMTP, and the new rows are appended to the Seen sheet.",
      },
    ],
    facts: [
      { key: "Trigger", value: "Cron — daily 08:00" },
      { key: "Sources", value: "8 feeds, RSS + JSON" },
      { key: "State", value: "Google Sheets — Seen" },
      { key: "Output", value: "SMTP digest email" },
      { key: "Tests", value: "22 unit tests" },
      { key: "Run cost", value: "R0 — free tiers only" },
    ],
    notes: [
      "Eligibility and relevance are deliberately separate: a role can be open to anyone and irrelevant, or perfect and locked to US citizens. Collapsing them into one filter loses that distinction.",
      "Bare developer, engineer and software are excluded as keywords — they match almost every listing and defeat the point of filtering.",
      "The filtering, dedupe and digest rules live in one dependency-free module covered by unit tests, rather than only inside opaque n8n Code nodes.",
      "Replaces roughly 30 minutes of manual board-checking a day.",
    ],
    stack: [
      "n8n",
      "JavaScript",
      "Google Sheets API",
      "SMTP",
      "RSS / JSON feeds",
      "Node test runner",
    ],
  },
  {
    id: "eventapp-lead-outreach",
    tone: "plum",
    name: "Lead Generation & Review-First Outreach",
    type: "n8n workflow · self-hosted",
    cadence: "Weekly · Mon 07:00",
    problem:
      "Finding South African event organisers and venues for EventApp, then writing each of them a personal intro, is hours of research nobody keeps up. This runs weekly, collects new leads from free public sources, and stages personalized Gmail drafts — so outreach becomes one approval click.",
    stages: [
      {
        label: "Trigger",
        detail: "Cron fires Monday at 07:00.",
      },
      {
        label: "Collect",
        detail:
          "Three public sources — 10times and Quicket South African event listings for organisers, and the OpenStreetMap Overpass API for conference and exhibition venues that publish a website.",
      },
      {
        label: "Enrich",
        detail:
          "Each organisation's homepage is fetched and scraped for a public contact email; rows that yield none are dropped.",
      },
      {
        label: "Dedupe",
        detail:
          "Anything whose email already exists in the Google Sheet is discarded, and each run is capped at 20 new leads.",
      },
      {
        label: "Draft",
        detail:
          "Survivors are appended to the sheet and a personalized Gmail draft is created for each. Nothing is sent at this point.",
      },
      {
        label: "Approve",
        detail:
          "A human reviews the sheet — mark a row approved to send it, skip to drop it, or edit the company and context fields to improve the email body.",
      },
      {
        label: "Send",
        detail:
          "A separate sub-flow, run on demand, emails every approved row and stamps it sent. A dedicated error workflow reports failures instead of letting a run die silently.",
      },
    ],
    facts: [
      { key: "Trigger", value: "Cron — weekly Mon 07:00" },
      { key: "Sources", value: "3 public sources" },
      { key: "State", value: "Google Sheets — Leads" },
      { key: "Output", value: "Gmail drafts, review-first" },
      { key: "Cap", value: "20 new leads / run" },
      { key: "Compliance", value: "POPIA-conservative" },
    ],
    notes: [
      "Human-in-the-loop is structural, not a setting: the scheduled flow can only create drafts, and sending lives in a second workflow that reads the approval column.",
      "Status lifecycle runs ready → drafted → approved → sent, with skip to exclude. The sheet is both the audit trail and the dedupe memory, keyed on email.",
      "POPIA-conscious by design — business and organisational contacts only, an opt-out line in every email, and low weekly volume behind a human check.",
      "The fragile parts, HTML scraping and email composition, are extracted into unit-tested modules rather than left inline.",
    ],
    stack: [
      "n8n",
      "JavaScript",
      "Google Sheets API",
      "Gmail API",
      "OpenStreetMap Overpass",
      "HTTP scraping",
    ],
  },
];

export const workflowToneStyle = (tone) => ({
  "--tone": `var(--c-${tone})`,
  "--tone-tint": `var(--c-${tone}-tint)`,
});
